import { z } from "zod";
import axios from "axios";
import { Log, UserManager, UserManagerSettings, User } from "oidc-client";
import React, { createContext, useContext, useState } from "react";
import { IConfig } from "../types/IConfig";
import { useLocation } from "react-router";

export type AuthContext = {
  userManager: UserManager | null;
  isAuthenticated: boolean;
  getUser: () => Promise<User | null>;
  login: () => Promise<void>;
  renewToken: () => Promise<User>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContext | null>(null);

export const authConfigSchema = z.object({
  stsAuthority: z.string().url(),
  clientId: z.string(),
  clientRoot: z.string().url(),
  clientScope: z.string(),
});
export type AuthConfig = z.infer<typeof authConfigSchema>;

interface AuthProviderProps {
  children: React.ReactNode;
  /* configuration for the oidc service */
  authConfig: AuthConfig | null;
  /**
   * Determines the redirect location after logging out from the OIDC server.
   * - 'jato-homepage': Redirects to jato.com. This is the default behavior and is desired for applications within the shared services.
   * - 'login-page': Redirects to the login page of the application from which the user logged out.
   * @default 'jato-homepage'
   */
  logoutType?: "login-page";
}

export const AuthProvider = ({
  children,
  logoutType = "login-page",
  authConfig = null,
}: AuthProviderProps) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const location = useLocation();

  const config = window.config as IConfig
    const oldSettings: UserManagerSettings = {
      authority: config?.stsAuthority ?? '',
      client_id: config?.clientId ?? '',
      redirect_uri: `${config?.clientRoot ?? ''}signin-callback.html`,
      silent_redirect_uri: `${config?.clientRoot ?? ''}silent-renew.html`,
      post_logout_redirect_uri: `${config?.clientRoot ?? ''}`,
      response_type: 'code',
      scope: config?.clientScope ?? '',
    }

  const [userManager] = useState(() => {
    const settings: UserManagerSettings = oldSettings/* {
      authority: authConfig.stsAuthority,
      client_id: authConfig.clientId,
      includeIdTokenInSilentRenew: false,
      post_logout_redirect_uri: authConfig.clientRoot,
      redirect_uri: `${authConfig.clientRoot}signin-callback.html`,
      response_type: "code",
      scope: authConfig.clientScope,
      silent_redirect_uri: `${authConfig.clientRoot}silent-renew.html`,
    } */;

    const userManager = new UserManager(settings);

    Log.logger = console;
    Log.level = Log.NONE;

    return userManager;
  });

  const getUser = async () => {
    if (userManager) {
      const user = await userManager.getUser();
      setIsAuthenticated(!!user);
      if (user && user.access_token) {
        axios.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${user.access_token}`;
        axios.defaults.headers.common["Accept"] = "application/json";
      }
      return user;
    }
    return null;
  };

  const login = () => userManager.signinRedirect({state: location});
  const renewToken = () => userManager.signinSilent();
  const logout = () => userManager.signoutRedirect();

  return (
    <AuthContext.Provider
      value={{
        getUser,
        userManager,
        isAuthenticated,
        login,
        renewToken,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};


export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
};
